import { useEffect, useState, useCallback } from 'react';
import { automationApi, type UpsellMapping, type UpsellOffer } from '@/lib/adminApi';
import { toast, Toaster } from 'sonner';
import { Card, CardHeader, CardBody } from '@/components/ui/card';
import { StatCard, PageHeader, Skeleton, EmptyState } from '@/components/ui/page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RefreshCw, Plus, Trash2, TrendingUp, CheckCircle, XCircle } from 'lucide-react';

export default function AutomationUpsellsPage() {
  const [mappings, setMappings] = useState<UpsellMapping[]>([]);
  const [offers, setOffers] = useState<UpsellOffer[]>([]);
  const [stats, setStats] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [newKw, setNewKw] = useState('');
  const [newProduct, setNewProduct] = useState('');

  const load = useCallback(async () => {
    try {
      const [m, a] = await Promise.all([
        automationApi.upsellMappings(),
        automationApi.upsellAnalytics(),
      ]);
      if (m.success) setMappings(m.data);
      if (a.success) { setOffers(a.data.recent); setStats(a.data.stats); }
    } catch (e: any) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addMapping = async () => {
    if (!newKw || !newProduct) return;
    try {
      await automationApi.createUpsellMapping({ source_keywords: newKw, upsell_product_name: newProduct });
      toast.success('Mapping added');
      setNewKw(''); setNewProduct('');
      load();
    } catch (e: any) { toast.error(e.message); }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-4">
        <PageHeader title="Upsell Manager" description="Product upsell engine" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <Toaster position="top-center" richColors />
      <PageHeader
        title="Upsell Manager"
        description="Product upsell engine — suggest related products to customers"
        action={<Button size="sm" variant="outline" onClick={load}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Offers Sent" value={stats.offered || 0} />
        <StatCard label="Accepted" value={stats.accepted || 0} />
        <StatCard label="Acceptance Rate" value={`${stats.acceptanceRate || 0}%`} />
        <StatCard label="Revenue" value={`EGP ${(stats.revenue || 0).toLocaleString()}`} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Product Mappings */}
        <Card>
          <CardHeader title="Product Mappings" description="Define which products to upsell based on keywords" />
          <CardBody>
            <div className="flex gap-2 mb-3">
              <Input placeholder="Keywords (e.g. بروتين,protein)" value={newKw} onChange={e => setNewKw(e.target.value)} className="text-xs h-8 flex-1" />
              <Input placeholder="Upsell product name" value={newProduct} onChange={e => setNewProduct(e.target.value)} className="text-xs h-8 flex-1" />
              <Button size="sm" onClick={addMapping}><Plus className="h-3.5 w-3.5" /></Button>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {mappings.length === 0 ? (
                <EmptyState title="No mappings. Click seedDefaultUpsellMappings() from API to populate defaults." />
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-zinc-500 uppercase border-b border-zinc-800">
                      <th className="text-left py-2 px-1">Keywords</th>
                      <th className="text-left py-2 px-1">Upsell Product</th>
                      <th className="text-center py-2 px-1">Priority</th>
                      <th className="text-center py-2 px-1">Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mappings.map(m => (
                      <tr key={m.id} className="border-b border-zinc-800/60 hover:bg-zinc-900/40">
                        <td className="py-1.5 px-1 text-xs font-mono text-zinc-400">{m.source_product_keywords}</td>
                        <td className="py-1.5 px-1 text-xs">{m.upsell_product_name}</td>
                        <td className="py-1.5 px-1 text-center text-xs">{m.priority}</td>
                        <td className="py-1.5 px-1 text-center">
                          {m.is_active ? <CheckCircle className="h-3.5 w-3.5 text-emerald-400 mx-auto" /> : <XCircle className="h-3.5 w-3.5 text-red-400 mx-auto" />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Recent Offers */}
        <Card>
          <CardHeader title="Recent Offers" description="Latest upsell offers and their status" />
          <CardBody>
            <div className="max-h-80 overflow-y-auto space-y-2">
              {offers.length === 0 ? (
                <EmptyState title="No offers yet. Upsells are sent after data collection completes." />
              ) : (
                offers.map(o => (
                  <div key={o.id} className="p-3 rounded-lg bg-elevated border border-white/[0.04] text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{o.product_name}</span>
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        o.status === 'accepted' ? 'bg-emerald-500/10 text-emerald-400' :
                        o.status === 'rejected' ? 'bg-red-500/10 text-red-400' :
                        'bg-amber-500/10 text-amber-400'
                      }`}>{o.status}</span>
                    </div>
                    {o.revenue_generated > 0 && (
                      <div className="text-xs text-emerald-400 mt-1">+ EGP {o.revenue_generated.toLocaleString()}</div>
                    )}
                    <div className="text-xs text-zinc-500 mt-1">{new Date(o.offered_at).toLocaleString()}</div>
                  </div>
                ))
              )}
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
